import { useEffect, useRef } from 'react';
import { Button, Empty, Skeleton, Spin } from 'antd';
import styled from 'styled-components';
import { ChatMode } from '@/shared/types';
import { ChatMessageComponent } from './ChatMessage';
import { ChatMessage, ChatMessageType } from '../model/types';

type Props = {
  messages?: Array<ChatMessage>;
  chatLoading?: boolean;
  messageLoading?: boolean;
  chatMode?: ChatMode;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isMyMessage: (createdBy: ChatMessageType, chatMode?: ChatMode) => boolean;
  streamedMessage?: ChatMessage | null;
};

export const ChatHistory = ({
  messages,
  messageLoading,
  chatLoading,
  isMyMessage,
  hasNextPage,
  fetchNextPage,
  chatMode,
  streamedMessage,
}: Props) => {
  const messagesWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesWrapperRef.current?.scrollTo(
      0,
      messagesWrapperRef.current?.scrollHeight,
    );
  }, [messages]);

  return (
    <Wrapper
      data-testid="chat-history"
      ref={messagesWrapperRef}
      style={{
        display: 'flex',
      }}
    >
      <Skeleton loading={chatLoading && chatMode !== ChatMode.Preview} active>
        {messages?.length ? (
          <>
            {hasNextPage && fetchNextPage && (
              <NextPageButtonWrapper>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => fetchNextPage()}
                  style={{ width: 'fit-content', fontSize: 12 }}
                >
                  Load older messages
                </Button>
              </NextPageButtonWrapper>
            )}
            {messages?.map((item) => (
              <ChatMessageComponent
                key={item.id}
                message={item}
                chatMode={chatMode}
                isMyMessage={isMyMessage}
              />
            ))}
            {messageLoading && (
              <Spin size="small" style={{ display: 'flex' }} />
            )}
            {streamedMessage && (
              <ChatMessageComponent
                key="streamed-message"
                message={streamedMessage}
                chatMode={chatMode}
                isMyMessage={isMyMessage}
              />
            )}
          </>
        ) : (
          <EmptyWrapper
            description={
              <span style={{ color: '#bbb' }}>
                {chatMode === ChatMode.Preview
                  ? 'This is just a preview'
                  : 'No messages to show'}
              </span>
            }
          />
        )}
      </Skeleton>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  max-height: 50vh;
  overflow-y: auto;
  padding: 20px 12px;
`;

const EmptyWrapper = styled(Empty)`
  color: #ccc;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const NextPageButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 10px 0 20px 0;
`;
